import { useCallback, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./walkthroughStyles.css";

const STORAGE_KEY = "qv-walkthrough";

/** Read completion map from localStorage. */
function getCompleted() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

/** Mark a tour as completed. */
function markCompleted(tourId) {
  const map = getCompleted();
  map[tourId] = Date.now();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

/** Check if this is the user's first visit (no tours completed). */
export function isFirstVisit() {
  return Object.keys(getCompleted()).length === 0;
}

/** Check if a specific tour has been completed. */
export function isTourCompleted(tourId) {
  return Boolean(getCompleted()[tourId]);
}

/** Reset all walkthrough progress. */
export function resetWalkthroughProgress() {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Hook that provides a `startTour` function.
 * Accepts an optional `setThemeTo` — switches to light mode during tours
 * for better visibility, restores the original theme when the tour ends.
 */
export function useWalkthrough(setThemeTo) {
  const activeDriver = useRef(null);
  const previousTheme = useRef(null);

  const startTour = useCallback((tourDef, options = {}) => {
    if (activeDriver.current) {
      try { activeDriver.current.destroy(); } catch { /* noop */ }
    }

    // Switch to light mode for better walkthrough visibility (unless skipped)
    if (setThemeTo && !options.skipThemeSwitch) {
      previousTheme.current = localStorage.getItem("theme") || "dark";
      if (previousTheme.current !== "light") {
        setThemeTo("light");
      }
    }

    const d = driver({
      showProgress: true,
      animate: true,
      overlayColor: "rgba(0, 0, 0, 0.65)",
      stagePadding: 6,
      stageRadius: 10,
      allowClose: true,
      smoothScroll: true,
      popoverClass: "qv-walkthrough-popover",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      onDestroyStarted: () => {
        markCompleted(tourDef.id);
        if (setThemeTo && previousTheme.current && previousTheme.current !== "light") {
          setThemeTo(previousTheme.current);
        }
        previousTheme.current = null;
        d.destroy();
        activeDriver.current = null;
      },
      steps: tourDef.steps,
    });

    activeDriver.current = d;
    d.drive();
  }, [setThemeTo]);

  const stopTour = useCallback(() => {
    if (activeDriver.current) {
      if (setThemeTo && previousTheme.current && previousTheme.current !== "light") {
        setThemeTo(previousTheme.current);
      }
      previousTheme.current = null;
      try { activeDriver.current.destroy(); } catch { /* noop */ }
      activeDriver.current = null;
    }
  }, [setThemeTo]);

  return { startTour, stopTour };
}

export default useWalkthrough;
